export interface User {
  id: number;
  email: string;
  name: string;
  website_ids: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserCreate {
  email: string;
  password: string;
  name: string;
}

export interface UserLogin {
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  user: User;
  token: string;
  message: string;
}

export interface Website {
  id: number;
  website_id: string;
  name: string;
  domain: string;
  description?: string;
  owner_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WebsiteCreate {
  website_id: string;
  name: string;
  domain: string;
  description?: string;
}

export interface WebsiteUpdate {
  name?: string;
  domain?: string;
  description?: string;
  is_active?: boolean;
}

export interface AnalyticsEvent {
  id: number;
  website_id: string;
  event_type: string;
  event_data: Record<string, any>;
  user_agent?: string;
  ip_address?: string;
  referrer?: string;
  session_id?: string;
  user_id?: string;
  timestamp: string;
  created_at: string;
}

export interface AnalyticsQuery {
  website_id?: string;
  event_type?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
  order_by?: "timestamp" | "created_at";
  order_direction?: "ASC" | "DESC";
}

export interface AnalyticsStats {
  total_events: number;
  unique_visitors: number;
  events_by_type: Record<string, number>;
  events_by_website: Record<string, number>;
  events_by_date: Record<string, number>;
}

export interface ApiResponse<T> {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Itinerary types
export const SUPPORTED_LANGUAGES = {
  en: "English",
  it: "Italiano",
  fr: "Français",
  es: "Español",
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

export interface AudioStep {
  id: number;
  title: Record<string, string>; // Dynamic languages: en, it, fr, es
  description: Record<string, string>; // Dynamic languages: en, it, fr, es
  audioUrl: Record<string, string>; // Dynamic languages: en, it, fr, es
  imageUrl?: string; // Step image URL
  location?: {
    latitude: number;
    longitude: number;
  };
}

export interface LanguageData {
  welcome: {
    title: string;
    subtitle: string;
  };
}

export interface ItineraryData {
  audioSteps: AudioStep[];
  languageData: Record<string, LanguageData>;
  logo: string;
}

export interface Itinerary {
  id: number;
  title: string;
  description?: string;
  data: ItineraryData;
  owner_id: number;
  version: number;
  is_active: boolean;
  visibility: 'public' | 'restricted';
  visibility_strategy?: string[] | string;
  created_at: string;
  updated_at: string;
}

export interface ItineraryCreate {
  title: string;
  description?: string;
  data: ItineraryData;
  visibility: 'public' | 'restricted';
  visibility_strategy?: string[] | string;
}

export interface ItineraryUpdate {
  title?: string;
  description?: string;
  data?: ItineraryData;
  visibility?: 'public' | 'restricted';
  visibility_strategy?: string[] | string;
}

export interface ItineraryStats {
  total: number;
  byOwner: number;
  latestVersion: number;
}

// Claim verification interfaces
export interface ClaimVerificationRequest {
  itinerary_id: number;
  claim: string;
}

export interface ClaimVerificationResponse {
  valid: boolean;
  message?: string;
}

// Translation interfaces
export interface TranslationRequest {
  text: string;
  sourceLanguage: string;
  targetLanguage: string;
}

export interface TranslationResponse {
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
}
